import _ from "lodash";
/*--external-- */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**--internal-- */
import { graphModes } from "Worksheet/modules/WorksheetConstants";

/**--relative--*/
import classes from "./FindHotspotField.scss";
import FindHotspot from "./FindHotspot";
import {
  convertDataToPixel,
  convertDataToPercentage,
  extractCorrectElementsFromData,
} from "./FindHotspot/FindHotspotUtils";

/**
 *
 * @param { mode, t }
 * @returns DragAndDropField Header
 */
const FindHotspotFieldHeader = ({ mode, t }) => {
  if (mode !== "create") return null;

  return (
    <div className={classes.headerContainer}>
      <div className={classes.leftContainer}>
        <div className="text-label-2">{t("worksheet:canvas")}</div>
      </div>
    </div>
  );
};

/**
 *
 * @param {*} props
 * @returns Find Hotspot Field
 */
const FindHotspotField = props => {
  const {
    value = {},
    t = () => "",
    mode = "preview",
    fieldId = "",
    questionId = "",
    resourceId = "",
    questionIndex = 0,
    customRef = () => {},
    isSubmitted = false,
    showSolution = false,
    isSubQuestion = false,
    fieldKey = "findHotspot",
    allowInteraction = false,
    subQuestionParentId = "",
    updateInputField = () => {},
    allowToShowHotspot = false,
    allowToSelectMultipleHotspot = false,
    showViewSolutionButton = false,
    isTeacherStudentViewMode = false,
    worksheetMode,
    isFromInternalTool = false,
  } = props;
  const componentRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [questionData, setQuestionData] = useState({
    elements: [],
    answers: [],
  });
  const [canvasDimension, setCanvasDimension] = useState({});

  // * Config data for Find hotspot question
  const config = {
    fieldId,
    fieldKey,
    questionId,
    resourceId,
    isSubmitted,
    showSolution,
    questionIndex,
    isSubQuestion,
    allowInteraction,
    allowToShowHotspot,
    subQuestionParentId,
    showViewSolutionButton,
    allowToSelectMultipleHotspot,
    isTeacherStudentViewMode,
  };

  // * Observing canvas resize in width/height and sending callback
  useEffect(() => {
    let resizeObserver;
    const observeCanvasResize = () => {
      const canvasId = `konva-canvas-container-id-${fieldId}-${mode}`;
      const targetElement = document.getElementById(canvasId);
      if (!targetElement) return;

      resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setCanvasDimension({ width, height });
        }
      });

      resizeObserver.observe(targetElement);
    };

    // * adding resize observer and disconnect cleanup of observer
    const observerTimeout = setTimeout(observeCanvasResize, 100);
    return () => {
      clearTimeout(observerTimeout);
      if (resizeObserver) resizeObserver.disconnect();
      resizeObserver = null;
    };
  }, [mode, fieldId, JSON.stringify(canvasDimension)]);

  // * Converting percentage data into pixel according to canvas width
  useEffect(() => {
    try {
      convertDataToPixel(value, fieldId, mode, setQuestionData);
    } catch (error) {}
  }, [
    mode,
    fieldId,
    isFullscreen,
    canvasDimension,
    mode === "preview" && _.isObject(value) ? JSON.stringify(value) : value,
  ]);

  // * Setting custom ref for worksheet wrapper
  useEffect(() => {
    customRef(componentRef.current);
  }, [customRef]);

  // * Updating local data and saving on server
  const handleDataUpdate = (
    updatedData,
    studentAnswers = [],
    findHotspotAnswers
  ) => {
    try {
      setQuestionData({ elements: updatedData, answers: studentAnswers });

      if (!_.includes(["create", "edit"], mode)) return;

      const convertedResponse = convertDataToPercentage(
        updatedData,
        studentAnswers,
        fieldId,
        mode
      );

      if (!convertedResponse) return;
      const { elements: dataInPercentage, answers } = convertedResponse;

      const { elements } = extractCorrectElementsFromData(
        mode,
        dataInPercentage
      );

      const params =
        mode === "create"
          ? { value: { elements }, findHotspotAnswers }
          : { value: { elements, answers } };

      updateInputField?.({
        params,
        isMutation: true,
        isDebounce: true,
      });
    } catch (error) {}
  };

  return (
    <div ref={componentRef} className={classes.container}>
      <FindHotspotFieldHeader t={t} mode={mode} />
      <FindHotspot
        t={t}
        key={fieldId}
        mode={mode}
        config={config}
        isFullscreen={isFullscreen}
        onUpdateData={handleDataUpdate}
        data={_.get(questionData, "elements", [])}
        answers={_.get(questionData, "answers", [])}
        findHotspotAnswers={_.get(props, "value.findHotspotAnswers", [])}
        onToggleFullscreen={() => setIsFullscreen(prev => !prev)}
        worksheetMode={worksheetMode}
        isFromInternalTool={isFromInternalTool}
      />
    </div>
  );
};

export default FindHotspotField;

FindHotspotField.displayName = "FindHotspotField";

FindHotspotField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  mode: PropTypes.oneOf(graphModes),
  updateInputField: PropTypes.func,
  fieldKey: PropTypes.string,
  questionIndex: PropTypes.number,
  isSubQuestion: PropTypes.bool,
  resourceId: PropTypes.string,
  questionId: PropTypes.string,
  fieldId: PropTypes.string,
  subQuestionParentId: PropTypes.string,
  allowToShowHotspot: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  allowToSelectMultipleHotspot: PropTypes.bool,
  isTeacherStudentViewMode: PropTypes.bool,
  showViewSolutionButton: PropTypes.bool,
};
